import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, ExternalLink, Save, Trash2, X as XIcon, Sparkles } from "lucide-react";
import { Api } from "../lib/api.js";

/**
 * Providers page — central place to manage LLM provider credentials.
 *
 * Each provider gets a card with:
 *   - colored status dot (jade if configured, parchment if not)
 *   - what it's good for (1-2 sentences)
 *   - sign-up link
 *   - inline credential editor / rotate / remove
 *
 * Tally sits at the top as a meta-row: when its key is set, every LLM turn
 * gets routed through Tally's recommender; without it, calls fall through
 * the Anthropic → OpenAI → DeepSeek order.
 */

interface ProviderSpec {
  key: string;                  // matches CREDENTIAL_KEYS in env.ts
  label: string;
  blurb: string;
  signupUrl: string;
  accent: string;               // tailwind text color class
  bg: string;                   // tailwind bg class for the icon swatch
  glyph: string;
}

const PROVIDERS: ProviderSpec[] = [
  {
    key: "ANTHROPIC_API_KEY",
    label: "Anthropic — Claude",
    blurb: "Family of conversational + reasoning models. Default for the mood concierge and comment evaluator. Haiku is fast + cheap, Sonnet is balanced, Opus is the heavyweight.",
    signupUrl: "https://console.anthropic.com/settings/keys",
    accent: "text-[#d97757]",
    bg: "bg-[#d9775720]",
    glyph: "C",
  },
  {
    key: "OPENAI_API_KEY",
    label: "OpenAI — GPT",
    blurb: "GPT-4o-mini for cheap structured extraction, GPT-4o for harder reasoning. Tally will route here when its bandit thinks they're the best fit for the request shape.",
    signupUrl: "https://platform.openai.com/api-keys",
    accent: "text-[#10a37f]",
    bg: "bg-[#10a37f20]",
    glyph: "GPT",
  },
  {
    key: "DEEPSEEK_API_KEY",
    label: "DeepSeek",
    blurb: "Very cheap, surprisingly capable. Strong choice for high-volume extraction (the comment evaluator) and quick disambiguation turns. Reasoner model competes with o1 at a fraction of the price.",
    signupUrl: "https://platform.deepseek.com/api_keys",
    accent: "text-[#4d6bfe]",
    bg: "bg-[#4d6bfe20]",
    glyph: "DS",
  },
  {
    key: "GEMINI_API_KEY",
    label: "Google Gemini",
    blurb: "Long context windows + multimodal. Useful when Tally tags a turn as 'large' context. Free tier is generous for personal use.",
    signupUrl: "https://aistudio.google.com/app/apikey",
    accent: "text-[#4285f4]",
    bg: "bg-[#4285f420]",
    glyph: "G",
  },
  {
    key: "XAI_API_KEY",
    label: "xAI — Grok",
    blurb: "Alternative reasoning model. Tally treats it as one more candidate in the bandit. Sign up if you have access; otherwise leave blank.",
    signupUrl: "https://console.x.ai/team/default/api-keys",
    accent: "text-[#cccccc]",
    bg: "bg-[#33333380]",
    glyph: "X",
  },
];

const TALLY_KEYS = ["TALLY_API_KEY", "TALLY_API_URL"];

export function Providers(): JSX.Element {
  const q = useQuery({
    queryKey: ["credentials"],
    queryFn: () => Api.credentials.list(),
    staleTime: 30_000,
  });
  const byKey = new Map((q.data?.credentials ?? []).map((c) => [c.key, c]));
  const tallyKey = byKey.get("TALLY_API_KEY");
  const tallyUrl = byKey.get("TALLY_API_URL");

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl text-ivory">Providers</h1>
        <span className="section-label">LLM routing & model access</span>
      </div>
      <div className="ornament-rule" />

      {/* Tally meta-row */}
      <div className="card p-5 mb-6 relative overflow-hidden">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-vermilion/30 border border-goldHi flex items-center justify-center flex-shrink-0">
            <Sparkles className="text-goldHi" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-ivory text-lg">Tally — routing intelligence</h2>
              <StatusDot set={!!tallyKey?.set} />
            </div>
            <p className="text-parchment text-sm mt-2 max-w-[80ch] leading-relaxed">
              When configured, every LLM turn (mood concierge, comment evaluator,
              anything else) asks Tally which provider+model to use based on the
              shape of the request. Telemetry feeds back so routing improves over
              time. Falls through to Anthropic → OpenAI → DeepSeek when absent.
            </p>
            <div className="mt-3 space-y-2">
              <CredentialRow keyName="TALLY_API_KEY" credential={tallyKey} signupUrl="https://api.tallyy.org" inline />
              <CredentialRow keyName="TALLY_API_URL" credential={tallyUrl} signupUrl="" inline plaintext />
            </div>
          </div>
        </div>
      </div>

      <div className="section-label mb-3">LLM providers</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {PROVIDERS.map((p) => (
          <ProviderCard key={p.key} spec={p} credential={byKey.get(p.key)} />
        ))}
      </div>

      <div className="mt-10 text-parchment/60 text-xs leading-relaxed max-w-[80ch]">
        Credentials are stored in Postgres (encrypted at rest by the host disk)
        and override anything in <code className="text-gold">/etc/wallace-tv.env</code>.
        Changes take effect immediately — no restart needed. Each key only
        appears here in masked form; the real value never leaves the server
        once saved.
      </div>
    </div>
  );
}

function ProviderCard({ spec, credential }: {
  spec: ProviderSpec;
  credential: { set: boolean; masked: string | null; source: string; updated_at: string | null } | undefined;
}): JSX.Element {
  return (
    <div className="card p-5">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg ${spec.bg} ${spec.accent} flex items-center justify-center flex-shrink-0 font-display text-sm font-semibold border border-hairline`}>
          {spec.glyph}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-display text-ivory text-base">{spec.label}</h3>
            <StatusDot set={!!credential?.set} />
          </div>
          <p className="text-parchment text-xs mt-2 leading-relaxed">{spec.blurb}</p>
          <div className="mt-3">
            <CredentialRow keyName={spec.key} credential={credential} signupUrl={spec.signupUrl} />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusDot({ set }: { set: boolean }): JSX.Element {
  return set ? (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-jade/20 border border-jade text-jade text-[10px] font-display tracking-smallcaps uppercase">
      <CheckCircle2 size={11} /> Connected
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-hairline text-parchment text-[10px] font-display tracking-smallcaps uppercase">
      <Circle size={11} /> Not set
    </span>
  );
}

function CredentialRow({
  keyName,
  credential,
  signupUrl,
  inline = false,
  plaintext = false,
}: {
  keyName: string;
  credential: { set: boolean; masked: string | null; source: string; updated_at: string | null } | undefined;
  signupUrl: string;
  inline?: boolean;
  plaintext?: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const save = useMutation({
    mutationFn: () => Api.credentials.save(keyName, value.trim()),
    onSuccess: () => {
      setEditing(false);
      setValue("");
      void qc.invalidateQueries({ queryKey: ["credentials"] });
    },
  });

  if (!editing) {
    return (
      <div className={`flex items-center gap-2 text-xs ${inline ? "" : ""}`}>
        <code className="text-parchment/60 text-[10px]">{keyName}</code>
        {credential?.set && credential.masked && (
          <span className="font-mono text-parchment">{credential.masked}</span>
        )}
        {credential?.set && credential.updated_at && (
          <span className="text-parchment/50 text-[10px]">
            · {new Date(credential.updated_at).toLocaleDateString()}
          </span>
        )}
        <div className="ml-auto flex gap-1">
          {signupUrl && !credential?.set && (
            <a
              href={signupUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="pill-ghost text-xs"
              title="Sign up / get an API key"
            >
              <ExternalLink size={12} /> Get key
            </a>
          )}
          <button
            onClick={() => setEditing(true)}
            className="pill-ghost text-xs"
            title={credential?.set ? "Rotate the saved value" : "Add a new key"}
          >
            <Save size={12} /> {credential?.set ? "Rotate" : "Add"}
          </button>
          {credential?.set && credential.source === "db" && (
            <button
              onClick={() => {
                if (confirm(`Remove the saved ${keyName}? It will fall back to whatever's in /etc/wallace-tv.env.`)) {
                  void Api.credentials.remove(keyName).then(() => qc.invalidateQueries({ queryKey: ["credentials"] }));
                }
              }}
              className="pill-ghost text-xs text-vermilionHi"
              title="Remove the DB override"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type={plaintext ? "text" : "password"}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={`New value for ${keyName}`}
        className="flex-1 bg-ink/60 border border-hairline rounded-md px-3 py-1.5 text-ivory focus:border-gold outline-none font-mono text-xs"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) save.mutate();
          if (e.key === "Escape") { setEditing(false); setValue(""); }
        }}
      />
      <button
        className="pill"
        disabled={value.trim().length === 0 || save.isPending}
        onClick={() => save.mutate()}
      >
        <Save size={12} /> Save
      </button>
      <button
        className="pill-ghost"
        onClick={() => { setEditing(false); setValue(""); }}
      >
        <XIcon size={12} />
      </button>
    </div>
  );
}

void TALLY_KEYS; // referenced in the page for clarity; suppress unused.
